import { useState } from 'react';

import { parseInlineMarkdown } from '@/util/parseInlineMarkdown';
import { Input } from './ui/input';

const parseNumber = (raw: string): number | undefined => {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return undefined;
  const parsed = Number(normalized);
  return isNaN(parsed) ? undefined : parsed;
};

// The app is French: comma is the expected separator, even though typing accepts both.
const formatForDisplay = (value?: string | number): string =>
  String(value ?? '').replace('.', ',');

const decimalsOf = (value: number): number => {
  const asString = String(value);
  // Notation exponentielle (1e-7) : pas de borne plutôt qu'une borne fausse.
  if (asString.includes('e')) return Infinity;
  return asString.split('.')[1]?.length ?? 0;
};

// What may be TYPED. Precision is deliberately NOT derived from `increment`: the KB's
// `incrément` is an arrow-key granularity, not a precision declaration. Poids declares
// `incrément 1`, so deriving one from the other would stop a patient typing 72,5 kg.
// Only `integer` constrains precision; otherwise any number of decimals is accepted,
// the way type="number" did.
const getAllowedPattern = (integer: boolean): RegExp =>
  integer ? /^-?\d*$/ : /^-?\d*[.,]?\d*$/;

interface ResponseNumberFieldProps {
  value?: string;
  label: string;
  onChange: (value?: number) => void;
  min?: number;
  max?: number;
  unit?: string;
  disabled?: boolean;
  integer?: boolean;
  alwaysShowLabel?: boolean;
  hasError?: boolean;
  increment?: number;
}

export const ResponseNumberField = ({
  value,
  label,
  min,
  max,
  unit,
  onChange,
  alwaysShowLabel = true,
  disabled,
  integer = true,
  hasError,
  increment,
}: ResponseNumberFieldProps) => {
  const halfWidth = Boolean(unit);
  const allowedPattern = getAllowedPattern(integer);
  const step = increment && increment > 0 ? increment : integer ? 1 : 0.1;
  // `draft` holds only what `value` can't round-trip: a trailing separator ("1,"),
  // trailing zeros ("1,50"), or a dot typed instead of a comma. Everything else is
  // derived from the prop.
  const [draft, setDraft] = useState<string | null>(null);
  const [lastValue, setLastValue] = useState(value);
  const numValue =
    value !== undefined && value !== '' ? Number(value) : undefined;

  // Adjusted during render rather than in an effect: when `value` changes from the
  // outside, drop in-progress text that no longer represents it. Typing keeps its
  // draft, because the value echoed back parses to the same number.
  if (value !== lastValue) {
    setLastValue(value);
    if (draft !== null && parseNumber(draft) !== numValue) setDraft(null);
  }

  const display = draft ?? formatForDisplay(value);

  // To allow keyboard incrementation
  const stepBy = (direction: 1 | -1) => {
    const current = parseNumber(display);
    const next = current === undefined ? min ?? 0 : current + direction * step;
    const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, next));
    // Round away float drift (0.2 + 0.1) without truncating precision the patient
    // typed themselves, so stepping 72,35 by 0,1 gives 72,45 rather than 72,5.
    const decimals = Math.max(decimalsOf(step), decimalsOf(current ?? 0));
    const stepped = Number.isFinite(decimals)
      ? Number(clamped.toFixed(decimals))
      : clamped;

    setDraft(formatForDisplay(stepped));
    onChange(stepped);
  };

  const isOutOfRange =
    numValue !== undefined &&
    ((min !== undefined && numValue < min) ||
      (max !== undefined && numValue > max));
  const showError = hasError || isOutOfRange;

  return (
    <div className="flex flex-col justify-center gap-1 text-sm w-full">
      {alwaysShowLabel && (
        <p className="text-indigo-900">{parseInlineMarkdown(label)}</p>
      )}
      <div className="flex items-center gap-2">
        <div className={halfWidth ? 'w-44' : 'w-full'}>
          <Input
            type="text"
            inputMode={integer ? 'numeric' : 'decimal'}
            placeholder=""
            value={display}
            disabled={disabled}
            aria-invalid={showError}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
              e.preventDefault();
              stepBy(e.key === 'ArrowUp' ? 1 : -1);
            }}
            // Dropping the draft on blur normalises what the patient left behind:
            // a dangling "1," or "-" collapses, and a typed dot becomes a comma.
            onBlur={() => setDraft(null)}
            onChange={(e) => {
              const newValue = e.target.value;
              if (!allowedPattern.test(newValue)) return;
              setDraft(newValue);
              onChange(parseNumber(newValue));
            }}
          />
        </div>
        {unit && <p className="text-gray-500 text-base shrink-0">{unit}</p>}
      </div>
    </div>
  );
};
